/**
 * Extensions that are safe to render inside the app, mapped to the mime type
 * the preview blob is forced to.
 *
 * A blob url is same origin with the document that created it, so anything
 * the browser is willing to execute from it (html, svg, xml) runs with access
 * to the token kept in localStorage. The blob type is what decides how the
 * browser treats the bytes, and axios copies it from the response
 * Content-Type, so the server's Content-Type is never trusted here, the type
 * is rebuilt from the file extension and anything unlisted is downloaded
 * instead of previewed.
 */
const safePreviewMimeTypes = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  bmp: "image/bmp",
  webp: "image/webp",
  txt: "text/plain",
  csv: "text/plain",
};

// Fallback release for a preview object url, the load listener below normally
// gets there first.
const previewObjectUrlTTL = 60000;

/**
 * Triggers a browser download for an already fetched file blob.
 *
 * Files must never be downloaded with <a href>, window.open or
 * window.location as those requests carry no Authorization header, which
 * exposes the file to anyone holding its path. Fetch the file through the
 * axios instance first, then hand the resulting blob to this method.
 */
export const triggerBlobDownload = (blob, fileName) => {
  if (!blob) return;
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName || "download";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
};

/**
 * Rebuilds a fetched file blob as a blob that is safe to render, returns null
 * when the file type is not on the preview whitelist, the caller is expected
 * to download it instead of rendering it.
 */
export const getSafePreviewBlob = (blob, fileName) => {
  if (!blob) return null;
  const extension = fileName?.split(".")?.pop()?.toLowerCase();
  const previewMimeType = (extension && safePreviewMimeTypes[extension]) || "";
  if (!previewMimeType) return null;

  return new Blob([blob], {type: previewMimeType});
};

/**
 * Opens the blank tab a preview is later pushed into, returns null when the
 * popup blocker refused it so the caller can report it.
 *
 * The tab has to be opened synchronously with the click, opening it after an
 * awaited fetch trips the popup blocker. window.open(url, "_blank",
 * "noopener") returns null and cannot be used here, so the opener reference is
 * severed manually instead to keep the child from scripting the app.
 */
export const openPreviewWindow = () => {
  const previewWindow = window.open("", "_blank");
  if (previewWindow)
    try {
      previewWindow.opener = null;
    } catch {
      // The browser refused to let the reference go, nothing else to do.
    }

  return previewWindow;
};

/**
 * Points an already opened tab at a preview blob and releases the object url
 * once the tab has loaded it, otherwise every previewed file stays pinned in
 * memory until the tab is closed.
 *
 * Revoking straight after the assignment is unreliable across browsers, hence
 * the load listener plus the bounded fallback.
 */
export const renderBlobInPreviewWindow = (previewWindow, blob) => {
  if (!previewWindow || !blob) return;
  const objectUrl = window.URL.createObjectURL(blob);
  const revokeHandler = () => window.URL.revokeObjectURL(objectUrl);
  try {
    previewWindow.addEventListener("load", revokeHandler, {once: true});
  } catch {
    // The tab is already navigating, the timeout below still releases it.
  }
  window.setTimeout(revokeHandler, previewObjectUrlTTL);
  previewWindow.location.href = objectUrl;
};
